import { useEffect } from "react";
import { useAppStore } from "@/core/store/useAppStore";

/**
 * Synchronises the user's theme preference (`light | dark | system`) from
 * Zustand to a `.dark` class on `<html>`.
 *
 * When `system` is selected, a `matchMedia` listener keeps the class in sync
 * with the OS preference. Renders nothing — this is a side-effect-only
 * component.
 */
export function ThemeProvider() {
  const theme = useAppStore((s) => s.theme);

  useEffect(() => {
    const root = document.documentElement;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");

    function apply(prefersDark: boolean) {
      if (prefersDark) {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    }

    if (theme === "dark") {
      apply(true);
    } else if (theme === "light") {
      apply(false);
    } else {
      // "system" — use OS preference and watch for changes
      apply(mq.matches);
    }

    // Only attach listener when tracking system preference
    function handleChange(e: MediaQueryListEvent) {
      if (useAppStore.getState().theme === "system") {
        apply(e.matches);
      }
    }

    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, [theme]);

  return null;
}
